import { useRef, useState } from "react";
import { Check, CalendarClock } from "lucide-react";
import type { NextAction } from "@/components/api/companiesApi";
import { todayLocal } from "@/lib/cms";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

interface NextActionEditorProps {
    nextAction: NextAction | null;
    /** Persist the next action (or null to clear). */
    onChange: (next: NextAction | null) => void;
}

const isValidDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

export function NextActionEditor({ nextAction, onChange }: NextActionEditorProps) {
    const [description, setDescription] = useState(nextAction?.description ?? "");
    const [dueDate, setDueDate] = useState(nextAction?.dueDate ?? "");
    const descriptionRef = useRef<HTMLInputElement>(null);

    // Re-sync local editor state when the persisted action changes identity
    // (e.g. an Undo restore). Done during render — the documented alternative to
    // a state-syncing effect — so it never clobbers in-progress typing.
    const [syncedFrom, setSyncedFrom] = useState(nextAction);
    if (syncedFrom !== nextAction) {
        setSyncedFrom(nextAction);
        setDescription(nextAction?.description ?? "");
        setDueDate(nextAction?.dueDate ?? "");
    }

    const valid = description.trim().length > 0 && isValidDate(dueDate);
    const dirty =
        description.trim() !== (nextAction?.description ?? "") ||
        dueDate !== (nextAction?.dueDate ?? "");
    const overdue = isValidDate(dueDate) && dueDate < todayLocal();

    const commit = () => {
        if (valid && dirty) {
            onChange({ description: description.trim(), dueDate });
        }
    };

    // The date picker commits immediately on selection (there is no blur), using
    // the freshly picked value rather than the not-yet-updated `dueDate` state.
    const handleDueChange = (next: string | null) => {
        const nextDue = next ?? "";
        setDueDate(nextDue);
        const trimmed = description.trim();
        const validNow = trimmed.length > 0 && isValidDate(nextDue);
        const dirtyNow =
            trimmed !== (nextAction?.description ?? "") ||
            nextDue !== (nextAction?.dueDate ?? "");
        if (validNow && dirtyNow) {
            onChange({ description: trimmed, dueDate: nextDue });
        }
    };

    const handleDone = () => {
        const previous = nextAction;
        onChange(null);
        toast("Next action completed", {
            description: previous?.description,
            action: previous
                ? { label: "Undo", onClick: () => onChange(previous) }
                : undefined,
        });
        // Immediately ready the editor for the follow-up action.
        setDescription("");
        setDueDate("");
        requestAnimationFrame(() => descriptionRef.current?.focus());
    };

    return (
        <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <div className="space-y-1.5">
                    <Label htmlFor="na-description">Next action</Label>
                    <Input
                        id="na-description"
                        ref={descriptionRef}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onBlur={commit}
                        placeholder="What's the next thing to do?"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="na-due">Due date</Label>
                    <DatePicker
                        id="na-due"
                        value={dueDate || null}
                        onChange={handleDueChange}
                        invalid={overdue}
                        className="sm:w-44"
                    />
                </div>
            </div>

            <div className="flex items-center gap-2">
                <Button size="sm" onClick={commit} disabled={!valid || !dirty} className="gap-1.5">
                    <CalendarClock className="h-4 w-4" />
                    {nextAction ? "Update action" : "Set action"}
                </Button>
                {nextAction && (
                    <Button size="sm" variant="outline" onClick={handleDone} className="gap-1.5">
                        <Check className="h-4 w-4" />
                        Done
                    </Button>
                )}
                {overdue && <span className="text-xs text-destructive font-medium">Overdue</span>}
            </div>
        </div>
    );
}
